import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Share2 } from 'lucide-react';

import { useGetArticleBySlugQuery, useTrackViewMutation } from '@/services/newsApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { markAsRead } from '@/store/news.slice';
import { ArticleHeader } from '@/components/news/ArticleHeader';
import { BlockRenderer, type ToolbarProps } from '@/components/news/blocks/BlockRenderer';
import { EngagementBar } from '@/components/news/EngagementBar';
import { RelatedArticles } from '@/components/news/RelatedArticles';
import { Loader } from '@/components/common/Loader';
import { useShareArticle } from '@/hooks/useShareArticle';

/**
 * Article detail screen — ported from
 * mobile-app/app/news-article/[slug].tsx (`feat/news-revised`).
 *
 * Web port of the swipe-to-next-article gesture (mobile uses
 * react-native-reanimated + Gesture.Pan). Implementation:
 *  - Local `currentSlug` state — initialized from URL param, updated by
 *    swipe without pushing history entries (URL stays in sync via
 *    navigate(replace) so share + reload still work).
 *  - Touch handlers track translateX with damping (0.3×) while dragging.
 *    Direction is locked in once the user has moved >30px horizontally
 *    AND horizontal motion dominates vertical — otherwise the gesture
 *    is treated as a normal scroll and ignored.
 *  - On release past SWIPE_THRESHOLD, animate the current article off
 *    screen, swap to prev/next, slide in from the opposite side.
 *
 * Scroll-depth tracking is a no-op (analytics not wired). The dwell-time
 * `trackView` mutation IS kept — qnews-api stores it for trending scores.
 * Share uses `t.me/share/url` instead of native Share.
 */

const SWIPE_ACTIVATE_PX = 30; // distance before locking direction
const SWIPE_COMMIT_PX = 80; // distance to commit to swipe-navigation
const ANIM_MS = 220;

export function ArticleDetail() {
  const { slug: urlSlug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('news');
  const locale = i18n.language || 'en';

  const articleSlugs = useAppSelector((s) => s.news.articleSlugs);

  // Local slug — swipe updates this directly, then we sync the URL via
  // navigate(replace) so deep links + reload land on the right article.
  const [currentSlug, setCurrentSlug] = useState(urlSlug);

  // External URL change (e.g. user navigated from News feed to a different
  // article) — resync local state. Skipped while a swipe animation is in
  // flight to avoid fighting the animation.
  const animatingRef = useRef(false);
  useEffect(() => {
    if (!animatingRef.current && urlSlug !== currentSlug) {
      setCurrentSlug(urlSlug);
    }
  }, [urlSlug, currentSlug]);

  const {
    data: article,
    isLoading,
    isError,
    refetch,
  } = useGetArticleBySlugQuery({ slug: currentSlug, locale }, { skip: !currentSlug });

  // Keep last-seen article in a ref so the screen doesn't blank during the
  // 50–300ms fetch right after a swipe.
  const lastArticleRef = useRef(article);
  useEffect(() => {
    if (article) lastArticleRef.current = article;
  }, [article]);
  const displayArticle = article ?? lastArticleRef.current;

  const [trackView] = useTrackViewMutation();
  const startTimeRef = useRef<number>(0);

  // Mark read + reset dwell timer on slug change.
  useEffect(() => {
    if (!article?._id) return;
    startTimeRef.current = Date.now();
    dispatch(markAsRead(article.slug));

    return () => {
      const dwellTimeMs = Date.now() - startTimeRef.current;
      if (dwellTimeMs > 2000) {
        trackView({ articleId: article._id, dwellTimeMs }).catch(() => {});
      }
    };
  }, [article?._id, article?.slug, dispatch, trackView]);

  const localized = useMemo(() => {
    if (!displayArticle) return null;
    return displayArticle.content.find((c) => c.locale === locale) ?? displayArticle.content[0];
  }, [displayArticle, locale]);

  const articleTitle = localized?.title ?? currentSlug;
  const handleShare = useShareArticle(displayArticle?._id ?? '', currentSlug, articleTitle);

  const publishedDate = useMemo(() => {
    if (!displayArticle?.publishedAt) return '';
    const diff = Date.now() - new Date(displayArticle.publishedAt).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return t('timeAgo.minutes', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('timeAgo.hours', { count: hours });
    const d = new Date(displayArticle.publishedAt);
    return d.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }, [displayArticle?.publishedAt, locale, t]);

  const renderToolbar = useCallback(
    ({ hasSections, showCollapseAll, toggleAll }: ToolbarProps) =>
      hasSections ? (
        <button
          type="button"
          onClick={toggleAll}
          className="mx-auto my-1 flex items-center gap-1.5 py-3 transition-opacity active:opacity-80"
        >
          <span className="text-[13px] font-semibold text-brand">
            {showCollapseAll
              ? t('collapseAll', { defaultValue: 'Collapse all' })
              : t('expandAll', { defaultValue: 'Expand all' })}
          </span>
          <span className="text-[10px]" style={{ color: showCollapseAll ? '#9a9a9a' : '#00e7df' }}>
            {showCollapseAll ? '▲' : '▼'}
          </span>
        </button>
      ) : null,
    [t],
  );

  // ---------- Swipe navigation ----------
  //
  // We bypass React state for the transform — React's auto-batching would
  // collapse the "jump off-screen / slide in" pair into a single paint and
  // animate from the wrong start position. Direct DOM writes via the article
  // ref let us interleave layout (no transition) and animation (with
  // transition) deterministically, frame by frame.

  const currentIndex = articleSlugs.indexOf(currentSlug);
  const prevSlug = currentIndex > 0 ? articleSlugs[currentIndex - 1] : null;
  const nextSlug =
    currentIndex >= 0 && currentIndex < articleSlugs.length - 1
      ? articleSlugs[currentIndex + 1]
      : null;
  const prevSlugRef = useRef(prevSlug);
  const nextSlugRef = useRef(nextSlug);
  prevSlugRef.current = prevSlug;
  nextSlugRef.current = nextSlug;

  const articleRef = useRef<HTMLElement>(null);
  const dragDxRef = useRef(0);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const horizontalLockRef = useRef(false);

  // Write transform directly to the DOM. `animate` toggles the CSS transition.
  const setTransform = useCallback((px: number, animate: boolean) => {
    const el = articleRef.current;
    if (!el) return;
    el.style.transition = animate ? `transform ${ANIM_MS}ms ease-out` : 'none';
    el.style.transform = `translate3d(${px}px, 0, 0)`;
  }, []);

  const swapTo = useCallback(
    (target: string, fromSide: 'left' | 'right') => {
      // Stage the new article off-screen on the OPPOSITE side from which the
      // old one exited, then animate it back to 0 (slide in).
      setCurrentSlug(target);
      navigate(`/news/${target}`, { replace: true });
      window.scrollTo({ top: 0, behavior: 'auto' });

      // Two RAFs so React commits the new content + browser paints the
      // off-screen position BEFORE the transition starts. Without this the
      // browser would short-circuit straight from the old off-screen offset
      // to 0, animating in the wrong direction.
      requestAnimationFrame(() => {
        setTransform(fromSide === 'left' ? -window.innerWidth : window.innerWidth, false);
        requestAnimationFrame(() => {
          setTransform(0, true);
          window.setTimeout(() => {
            animatingRef.current = false;
          }, ANIM_MS + 20);
        });
      });
    },
    [navigate, setTransform],
  );

  const onTouchStart = (e: React.TouchEvent) => {
    if (animatingRef.current) return;
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    horizontalLockRef.current = false;
    dragDxRef.current = 0;
    // Drop any in-flight transition so the live drag is instant.
    setTransform(0, false);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const dx = e.touches[0].clientX - touchStartXRef.current;
    const dy = e.touches[0].clientY - touchStartYRef.current;

    if (!horizontalLockRef.current) {
      if (Math.abs(dx) > SWIPE_ACTIVATE_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
        horizontalLockRef.current = true;
      } else if (Math.abs(dy) > SWIPE_ACTIVATE_PX) {
        // Vertical scroll won — abort horizontal interpretation.
        touchStartXRef.current = null;
        touchStartYRef.current = null;
        return;
      } else {
        return;
      }
    }

    // Apply damping (0.3× like mobile). Resist when no neighbour exists in
    // that direction so the user feels a stiff bounce instead of a smooth
    // pull-into-nothing.
    let damped = dx * 0.3;
    if (dx > 0 && !prevSlugRef.current) damped = dx * 0.05;
    if (dx < 0 && !nextSlugRef.current) damped = dx * 0.05;
    dragDxRef.current = dx;
    setTransform(damped, false);
  };

  const onTouchEnd = () => {
    const wasHorizontal = horizontalLockRef.current;
    const dx = dragDxRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    horizontalLockRef.current = false;
    dragDxRef.current = 0;

    if (!wasHorizontal) return;

    if (dx > SWIPE_COMMIT_PX && prevSlugRef.current) {
      const target = prevSlugRef.current;
      animatingRef.current = true;
      setTransform(window.innerWidth, true);
      window.setTimeout(() => swapTo(target, 'left'), ANIM_MS);
    } else if (dx < -SWIPE_COMMIT_PX && nextSlugRef.current) {
      const target = nextSlugRef.current;
      animatingRef.current = true;
      setTransform(-window.innerWidth, true);
      window.setTimeout(() => swapTo(target, 'right'), ANIM_MS);
    } else {
      // Snap back to 0.
      setTransform(0, true);
    }
  };

  // ---------- Render ----------

  if (isLoading && !displayArticle) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (isError && !displayArticle) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-8 py-16">
        <p className="text-center text-base text-fg-muted">
          {t('detailPage.errorTitle', { defaultValue: "Couldn't load article" })}
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-full bg-bg-secondary px-6 py-3 text-sm font-semibold text-brand transition-opacity active:opacity-80"
        >
          {t('detailPage.errorRetry', { defaultValue: 'Retry' })}
        </button>
      </div>
    );
  }

  if (!displayArticle || !localized) return null;

  return (
    <article
      ref={articleRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      // Hint the browser to lift onto the GPU compositor so the slide stays
      // smooth even with a heavy article body in the DOM. The transform
      // itself is driven imperatively by setTransform() via articleRef so
      // we never fight React's auto-batching on the swipe animation.
      style={{ willChange: 'transform' }}
      className="flex flex-col pb-10"
    >
      <ArticleHeader article={displayArticle} localized={localized} />

      <div className="mb-2 flex items-center justify-between px-5">
        <span className="text-[13px] text-fg-muted">{publishedDate}</span>
        <button
          type="button"
          onClick={handleShare}
          aria-label={t('share', { defaultValue: 'Share' })}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-bg-secondary text-brand transition-opacity active:opacity-80"
        >
          <Share2 className="h-4 w-4" />
        </button>
      </div>

      {/* Il corpo dell'articolo su una SUPERFICIE, non sulla texture a scaglie.
          È lo stesso principio della card-finestra (ADR-0038, punto 4): un
          motivo dietro una lista si sopporta, dietro un paragrafo lungo no —
          leggere è la cosa che si fa più a lungo in tutta l'app, e le scaglie
          competono con le lettere per ogni riga.
          `bg-bg`, il teal più scuro, come ogni altra card di sezione.
          Arrotondata solo IN ALTO: sotto continua fino in fondo alla schermata,
          e chiudere il bordo lì lascerebbe una fessura di texture sotto
          l'ultimo paragrafo.
          Bordo solo superiore invece di `card-edge`: qui la superficie ha un
          solo lato visibile, e un bordo che gira attorno a qualcosa che esce
          dallo schermo si legge come un errore di ritaglio. */}
      <div className="divider-edge mt-3 flex-1 rounded-t-2xl border-t bg-bg pb-4 pt-2">
        <BlockRenderer blocks={localized.blocks} renderToolbar={renderToolbar} />

        <EngagementBar article={displayArticle} onShare={handleShare} />
      </div>

      {displayArticle.primaryTag && (
        <RelatedArticles
          tagSlug={displayArticle.primaryTag.slug}
          currentArticleSlug={displayArticle.slug}
          locale={locale}
        />
      )}

      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-2 mt-4 rounded-full bg-bg-secondary px-6 py-3 text-sm font-semibold text-brand transition-opacity active:opacity-80"
        >
          ← {t('detailPage.back', { defaultValue: 'Back to News' })}
        </button>
      </div>
    </article>
  );
}
